import { objects, time } from "@dinahmoe/dmafjs-core";

import { visualNodes } from "./nodes.js";

import { flush, remove } from "./helpers/elements.helper.js";
import { flushInitialResizes } from "./env/visual-super.environment.js";
import { runPendingUpdates } from "./helpers/styles.helper.js";
import { Action } from "./libs/enums/actions.enums.js";
import { VisualEnvironmentNode } from "./libs/interfaces/node.interface.js";

class VisualWebEngine {
  #isVisible = true;
  #windowFocused = true;
  #pendingNodesQueue = [];
  #queuedPendingNodes = {};

  constructor() {
    if (typeof document !== "undefined") {
      this.#injectBaselineStyles();
      requestAnimationFrame(this.run.bind(this));
    }
  }

  #injectBaselineStyles() {
    const style = document.createElement("style");
    style.textContent = [
      "[data-namespace]::-webkit-scrollbar { width: 0 !important; height: 0 !important; }",
      "svg[data-namespace] * { pointer-events: none; }",
    ].join("\n");
    document.head.appendChild(style);
  }

  create(config, message) {
    config.patch = {};
    config.action = Action.ADD;

    this.#addToPendingNodesQueue(config);
  }

  update(message) {
    const { uid, key, value } = message;

    const pendingNode = this.#queuedPendingNodes[uid];
    if (pendingNode && pendingNode?.action !== Action.REMOVE) {
      if (key === "tween") {
        const existing = pendingNode.patch[key];
        pendingNode.patch[key] = existing ? [].concat(existing, value) : [value];
      } else {
        pendingNode.patch[key] = value;
      }
      return;
    }

    const nodeUpdateData = {
      uid,
      action: Action.UPDATE,
      patch: {
        [key]: value,
      },
    };
    this.#addToPendingNodesQueue(nodeUpdateData);
  }

  destroy(message) {
    const { uid } = message;
    // the node has not yet been mounted, ignore
    const pendingNode = this.#queuedPendingNodes[uid];
    if (pendingNode && pendingNode?.action === Action.ADD) {
      pendingNode.action = Action.IGNORE;
      return;
    }

    const nodeRemoveData = {
      uid,
      action: Action.REMOVE,
    };
    this.#addToPendingNodesQueue(nodeRemoveData);
  }

  #addToPendingNodesQueue(pendingNode) {
    this.#pendingNodesQueue.push(pendingNode);
    this.#queuedPendingNodes[pendingNode.uid] = pendingNode;
  }

  // called by root on browser's visibilitychange event
  setVisibility(visibility) {
    if (visibility === "hidden") {
      this.#isVisible = false;
      this.runToo();
      return;
    }

    this.#isVisible = true;
  }

  // called by root on browser's focus/blur events
  setWindowFocus(isFocused) {
    this.#windowFocused = isFocused;
    if (!isFocused) {
      this.runToo();
    }
  }

  runToo() {
    time.runUpdate();
    this.updateDom();

    if (this.#isVisible && this.#windowFocused) {
      return;
    }

    setTimeout(this.runToo.bind(this), 10);
  }

  run() {
    time.runUpdate();
    this.updateDom();
    requestAnimationFrame(this.run.bind(this));
  }

  updateDom() {
    if (this.#pendingNodesQueue.length === 0) {
      return;
    }

    // cloning the queue is necessary because the queue can be modified and go into a feedback loop
    const pendingNodesQueue = objects.clone(this.#pendingNodesQueue);
    this.#pendingNodesQueue = [];
    this.#queuedPendingNodes = {};

    for (const pendingNode of pendingNodesQueue) {
      switch (pendingNode.action) {
        case Action.ADD: {
          this.#addNode(pendingNode);
          break;
        }
        case Action.UPDATE: {
          this.#updateNode(pendingNode);
          break;
        }
        case Action.REMOVE: {
          this.#removeNode(pendingNode);
          break;
        }
      }
    }

    runPendingUpdates();
    flush();
    flushInitialResizes();
  }

  #addNode(pendingNode) {
    const eNodeParent = visualNodes.get(pendingNode.props.parentUid);

    //console.error("engine addNode", JSON.parse(JSON.stringify(pendingNode)))

    // TODO fix root parent exception
    if (!eNodeParent && pendingNode.namespace !== "visual:root") {
      console.warn(
        `There is no eNodeParent "${pendingNode.props?.parentUid}" for "${pendingNode.uid}" (${pendingNode.namespace}) to add element to!`,
      );
      return;
    }

    const eNode = new VisualEnvironmentNode(pendingNode);
    visualNodes.set(eNode.uid, eNode);

    eNode.envImplementation.create(eNode);
  }

  #updateNode(pendingNode) {
    const eNode = visualNodes.get(pendingNode.uid);
    if (!eNode) {
      return;
    }

    for (const key in pendingNode.patch) {
      eNode.envImplementation.update(eNode, { key, value: pendingNode.patch[key] });
    }
  }

  #removeNode(pendingNode) {
    const eNode = visualNodes.get(pendingNode.uid);
    if (!eNode) {
      return;
    }

    remove(eNode.uid);
  }

}

import { browserRuntime } from "@dinahmoe/dmafjs-web";

const visualWebEngine = new VisualWebEngine();
browserRuntime.registerEngine("visual", visualWebEngine);
export { VisualWebEngine, visualWebEngine };
